import dayjs from "dayjs";
import { type ReactNode, useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { z } from "zod";

import { Badge } from "@/components/ui/badge";
import { FieldError, Label } from "@/components/ui/field";
import { Link } from "@/components/ui/link";
import { NumberField, NumberInput } from "@/components/ui/number-field";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Text } from "@/components/ui/text";
import {
  type RouterInputs,
  SampleTempType,
  type TAccession,
  TempUnit,
  type TSample,
} from "@/lib/types";
import { format } from "@/lib/utils";
import {
  shipperEnumTempsToPayload,
  shipperExplicitTempsToPayload,
} from "../lib/sample-temp-form";
import { requiresEnumTempAtReceive } from "../lib/sample-temp-mode";
import { sampleTempTypeOptions } from "../lib/sample-temp-options";
import { useReceiveForm } from "./ReceiveFormProvider";

const enumOptions = sampleTempTypeOptions;

const baseSchema = z.object({});

const enumSchema = z.object({
  sampleTempEnums: z
    .object({
      id: z.number(),
      type: z.enum(SampleTempType),
    })
    .array()
    .min(1, "Required"),
});

const explicitSchema = z.object({
  sampleTempValues: z
    .object({
      id: z.number(),
      value: z.number(),
      units: z.enum(TempUnit),
    })
    .array()
    .min(1, "Required"),
});

const mergedSchema = baseSchema
  .extend(enumSchema.shape)
  .extend(explicitSchema.shape);

type FormValues = z.input<typeof mergedSchema>;
export type FormOutput = z.output<typeof mergedSchema>;

export function ShipperValidationForm({
  accession,
  samples,
}: {
  accession: TAccession;
  samples: TSample[];
}) {
  const { reset } = useFormContext<FormValues>();
  const { registerConverter } = useReceiveForm();

  useEffect(() => {
    registerConverter("shipper-validation", convertShipperValidationValues);
  }, [registerConverter]);

  const hoursSince = dayjs().diff(accession.packedAt, "hour", true);
  let unit = "hours";
  let timeSince = hoursSince;
  if (hoursSince < 1) {
    timeSince =
      hoursSince < 1 ? dayjs().diff(accession.packedAt, "minutes", true) : -1;
    unit = "minutes";
  }

  // useEffect(() => {
  //   return () => {
  //     reset({ sampleTempEnums: [], sampleTempValues: [] });
  //   };
  // }, [reset]);

  return (
    <>
      <Text size="2">Packed: </Text>
      {accession.packedAt ? (
        <Text
          size="2"
          className={hoursSince > 48 ? "text-destructive" : "text-success"}
        >{`${timeSince.toFixed()} ${unit} ago`}</Text>
      ) : (
        <Text size="2" className="text-destructive">
          No packing time
        </Text>
      )}
      <Separator className="my-2" />
      {requiresEnumTempAtReceive(accession.packedAt) ? (
        <EnumSampleForm samples={samples} />
      ) : (
        <ExplicitSampleForm samples={samples} />
      )}
    </>
  );
}

function EnumSampleForm({ samples }: { samples: TSample[] }) {
  const [showSamples, setShowSamples] = useState(false);
  const {
    setValue,
    formState: { errors },
  } = useFormContext<FormValues>();
  const { mergeSchema } = useReceiveForm();

  useEffect(() => {
    mergeSchema(enumSchema);
  }, [mergeSchema]);

  const onEnumSelectChange = (val: { value: SampleTempType } | null) => {
    if (val) {
      samples.forEach((sample, idx) => {
        setValue(`sampleTempEnums.${idx}`, { id: sample.id, type: val.value });
      });
    }
  };
  return showSamples ? (
    <>
      <Link
        className="text-xs cursor-pointer"
        onPress={() => setShowSamples(false)}
      >
        &larr; Back
      </Link>
      <div className="flex flex-col divide-y">
        {samples.map((s, idx) => (
          <EnumSampleTemp key={s.id} sample={s} idx={idx} />
        ))}
      </div>
    </>
  ) : (
    <>
      {/* <SelectField
        isRequired
        // placeholder="Select temperature"
        onSelectionChange={(value) =>
          onEnumSelectChange({ value: value as SampleTempType })
        }
      > */}
      {/* <Label>Temperature of samples</Label> */}
      <Select
        isRequired
        placeholder="Select temperature"
        onSelectionChange={(value) =>
          onEnumSelectChange({ value: value as SampleTempType })
        }
      >
        <Label>Temperature of samples</Label>
        <SelectTrigger />
        <SelectList>
          <SelectItem id={SampleTempType.Ambient} textValue="Ambient">
            Ambient (2-25°C)
          </SelectItem>
          <SelectItem id={SampleTempType.Refrigerated} textValue="Refrigerated">
            Refrigerated (2-8°C)
          </SelectItem>
          <SelectItem id={SampleTempType.Frozen} textValue="Frozen">
            Frozen {"(<= 0°C)"}
          </SelectItem>
        </SelectList>
        <FieldError />
      </Select>
      <Link
        className="text-xs cursor-pointer"
        onPress={() => setShowSamples(true)}
      >
        Samples have various temperatures
      </Link>
    </>
  );
}

function ExplicitSampleForm({ samples }: { samples: TSample[] }) {
  const [showSamples, setShowSamples] = useState(false);
  const { setValue, control } = useFormContext<FormValues>();
  const { mergeSchema } = useReceiveForm();

  useEffect(() => {
    mergeSchema(explicitSchema);
  }, [mergeSchema]);

  const onInputChange = (value: number) => {
    if (value !== undefined) {
      samples.forEach((sample, idx) => {
        setValue(`sampleTempValues.${idx}`, {
          id: sample.id,
          value: value,
          units: TempUnit.C,
        });
      });
    }
  };

  return showSamples ? (
    <>
      <Link className="text-xs" onPress={() => setShowSamples(false)}>
        &larr; Back
      </Link>
      <div className="flex flex-col divide-y pt-1">
        {samples.map((s, idx) => (
          <ExplicitSampleTemp key={s.id} sample={s} idx={idx} />
        ))}
      </div>
    </>
  ) : (
    <>
      <Controller
        control={control}
        name="sampleTempValues"
        render={({ field, fieldState: { invalid, error } }) => (
          <NumberField
            onChange={onInputChange}
            className="w-48"
            isInvalid={invalid}
            onBlur={field.onBlur}
            validationBehavior="aria"
          >
            <Label>Temperature of samples</Label>
            <NumberInput />
            <FieldError>{error?.message}</FieldError>
          </NumberField>
        )}
      />
      <Link className="text-xs" onPress={() => setShowSamples(true)}>
        Samples have various temperatures
      </Link>
    </>
  );
}

function EnumSampleTemp({ idx, sample }: { sample: TSample; idx: number }) {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<FormValues>();

  const getValue = (field: { value: SampleTempType }) => {
    return enumOptions.find((o) => o.value === field.value);
  };

  return (
    <>
      <input
        type="hidden"
        {...register(`sampleTempEnums.${idx}.id`, { valueAsNumber: true })}
        value={sample.id}
      />
      <SampleRow sample={sample}>
        <Controller
          name={`sampleTempEnums.${idx}.type`}
          control={control}
          render={({ field, fieldState: { invalid, error } }) => (
            <div>
              <Select
                isInvalid={invalid}
                aria-label="Temperature of sample"
                selectedKey={getValue(field)?.value ?? ""}
                onSelectionChange={(value) =>
                  field.onChange(value as SampleTempType)
                }
              >
                <SelectTrigger />
                <SelectList>
                  <SelectItem id={SampleTempType.Ambient} textValue="Ambient">
                    Ambient (2-25°C)
                  </SelectItem>
                  <SelectItem
                    id={SampleTempType.Refrigerated}
                    textValue="Refrigerated"
                  >
                    Refrigerated (2-8°C)
                  </SelectItem>
                  <SelectItem id={SampleTempType.Frozen} textValue="Frozen">
                    Frozen {"(<= 0°C)"}
                  </SelectItem>
                </SelectList>
              </Select>
            </div>
          )}
        />
      </SampleRow>
    </>
  );
}

function ExplicitSampleTemp({ idx, sample }: { sample: TSample; idx: number }) {
  const { register, control } = useFormContext<FormValues>();

  return (
    <>
      <input
        type="hidden"
        {...register(`sampleTempValues.${idx}.id`, { valueAsNumber: true })}
        value={sample.id}
      />
      <input
        type="hidden"
        {...register(`sampleTempValues.${idx}.units`)}
        value={TempUnit.C}
      />
      <SampleRow sample={sample}>
        <div className="flex items-center gap-1 justify-end">
          <Controller
            control={control}
            name={`sampleTempValues.${idx}.value`}
            render={({ field, fieldState: { invalid, error } }) => (
              <NumberField
                isInvalid={invalid}
                value={field.value === undefined ? Number.NaN : field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                aria-label="Sample temperature"
                validationBehavior="aria"
                className="max-w-32"
              >
                <NumberInput />
                <FieldError>{error?.message}</FieldError>
              </NumberField>
            )}
          />
        </div>
      </SampleRow>
    </>
  );
}

function SampleRow({
  sample,
  children,
}: {
  sample: TSample;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 py-2 mt-1">
      {sample.displayId && (
        <Badge size="sm" variant="outline" className="shrink-0">
          {sample.displayId}
        </Badge>
      )}
      <div className="shrink-0 flex-1 flex flex-col min-w-0">
        <Text truncate size="xs">
          {sample.tube.name}
        </Text>
        <Text size="xs" muted>
          {format(sample.collection)}
        </Text>
      </div>
      {children}
    </div>
  );
}

export function convertShipperValidationValues({
  sampleTempEnums = [],
  sampleTempValues = [],
}: FormOutput) {
  return {
    sampleTemps: [
      ...shipperEnumTempsToPayload(sampleTempEnums),
      ...shipperExplicitTempsToPayload(sampleTempValues),
    ],
  };
}
